import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchMateriaallijstenNietGereed,
  type ControleBucket,
  type MateriaallijstControleRow,
} from '@/api/controle';

const STALE_DAYS = 30;

const BUCKET_ORDER: ControleBucket[] = ['wachtend', 'beschikbaar', 'concept'];

const BUCKET_META: Record<
  ControleBucket,
  { label: string; sub: string; dot: string }
> = {
  wachtend: {
    label: 'Wachtend',
    sub: 'bevestigd maar geen stock gereserveerd',
    dot: 'bg-orange-500',
  },
  beschikbaar: {
    label: 'Beschikbaar',
    sub: 'gereserveerd, wacht op validatie magazijn',
    dot: 'bg-amber-400',
  },
  concept: {
    label: 'Concept',
    sub: 'geüpload maar nooit bevestigd / gereserveerd',
    dot: 'bg-slate-300',
  },
};

function ageLabel(days: number | null): string {
  if (days === null) return '—';
  if (days === 0) return 'vandaag';
  return `${days} d`;
}

export function MateriaallijstenNietGereed() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<MateriaallijstControleRow[]>([]);
  const [counts, setCounts] = useState<Record<ControleBucket, number>>({
    concept: 0,
    beschikbaar: 0,
    wachtend: 0,
  });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState<ControleBucket | 'all'>('all');
  const [staleOnly, setStaleOnly] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchMateriaallijstenNietGereed()
      .then((data) => {
        if (!active) return;
        setRows(data.rows);
        setCounts(data.counts);
        setTotal(data.total);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Laden mislukt');
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (bucketFilter !== 'all' && r.bucket !== bucketFilter) return false;
      if (staleOnly && (r.ageDays === null || r.ageDays < STALE_DAYS)) return false;
      if (q) {
        const hay = `${r.werf} ${r.lijst ?? ''} ${r.pickingName ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, bucketFilter, staleOnly]);

  const grouped = useMemo(() => {
    const map: Record<ControleBucket, MateriaallijstControleRow[]> = {
      wachtend: [],
      beschikbaar: [],
      concept: [],
    };
    for (const r of filtered) map[r.bucket].push(r);
    return map;
  }, [filtered]);

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Laden…</div>;
  }
  if (error) {
    return (
      <div className="p-8">
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard value={total} label="Totaal niet gereed" valueClass="text-td-navy" />
        <SummaryCard value={counts.concept} label="Concept" dot="bg-slate-300" valueClass="text-slate-400" />
        <SummaryCard value={counts.beschikbaar} label="Beschikbaar" dot="bg-amber-400" valueClass="text-amber-500" />
        <SummaryCard value={counts.wachtend} label="Wachtend · geen stock" dot="bg-orange-500" valueClass="text-orange-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[12rem] max-w-xs">
          <svg className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek werf of lijst…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/30"
          />
        </div>
        <div className="flex gap-2 text-xs">
          {(['all', 'concept', 'beschikbaar', 'wachtend'] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBucketFilter(b)}
              className={`px-3 py-1.5 rounded-full font-medium transition ${
                bucketFilter === b
                  ? 'bg-td-navy text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {b === 'all' ? 'Alle' : BUCKET_META[b].label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
          <input type="checkbox" className="rounded" checked={staleOnly} onChange={(e) => setStaleOnly(e.target.checked)} />
          alleen ouder dan {STALE_DAYS} dagen
        </label>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-400">
          Geen materiaallijsten gevonden voor deze filter.
        </div>
      ) : (
        BUCKET_ORDER.map((bucket) => {
          const bucketRows = grouped[bucket];
          if (bucketRows.length === 0) return null;
          const meta = BUCKET_META[bucket];
          return (
            <div key={bucket}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
                <h2 className="text-sm font-bold text-slate-700">
                  {meta.label} — {meta.sub}
                </h2>
                <span className="text-xs text-slate-400">
                  {bucketRows.length} {bucketRows.length === 1 ? 'lijst' : 'lijsten'}
                </span>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400">
                    <tr>
                      <th className="text-left font-semibold px-4 py-2.5 w-1/4">Werf</th>
                      <th className="text-left font-semibold px-4 py-2.5">Materiaallijst</th>
                      <th className="text-left font-semibold px-4 py-2.5 w-36">Picking</th>
                      <th className="text-right font-semibold px-4 py-2.5 w-24">Ouderdom</th>
                      <th className="px-4 py-2.5 w-32" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bucketRows.map((r) => {
                      const stale = r.ageDays !== null && r.ageDays >= STALE_DAYS;
                      const clickable = r.projectId !== null;
                      return (
                        <tr
                          key={r.pickingId}
                          onClick={clickable ? () => navigate(`/project/${r.projectId}`) : undefined}
                          className={`${clickable ? 'cursor-pointer' : ''} ${
                            stale ? 'bg-rose-50/40 hover:bg-rose-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          <td className="px-4 py-3 font-medium text-td-navy">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="truncate">{r.werf}</span>
                              {r.isEasykit && (
                                <span className="px-1.5 py-0.5 rounded bg-td-purple/10 text-td-purple text-[10px] font-semibold shrink-0">
                                  EK
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {r.lijst ?? <span className="text-slate-400 italic">— geen lijstnaam —</span>}
                          </td>
                          <td className="px-4 py-3 text-slate-400 font-mono text-xs">{r.pickingName ?? '—'}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={stale ? 'text-rose-600 font-semibold' : 'text-slate-500'}>
                              {ageLabel(r.ageDays)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {clickable && <span className="text-td-blue text-xs font-medium">Open project ›</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      <p className="text-[11px] text-slate-400">
        ⌁ Rijen ouder dan {STALE_DAYS} dagen zijn rood — waarschijnlijk verlaten lijsten. Sinds de
        reserve-on-upload wijziging komt elke nieuwe lijst meteen op Beschikbaar of Wachtend, niet meer op
        Concept.
      </p>
    </div>
  );
}

function SummaryCard({
  value,
  label,
  dot,
  valueClass = 'text-td-navy',
}: {
  value: number;
  label: string;
  dot?: string;
  valueClass?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className={`text-3xl font-bold ${valueClass}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
        {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />}
        {label}
      </div>
    </div>
  );
}
